import { useCallback } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  chatProgrammaticMessageAtom,
  screenataAssistantOpenAtom,
  screenataPageContextAtom,
} from '@/stores/screenataPageContextStore'
import { getWorkspaceIdFromPath } from '@/lib/screenata-phase'
import { ChatThreadScope } from './ChatThreadManager'
import { createThreadAtom } from './store/chatThreadStore'

type AskAiThreadScope = {
  workspaceId?: string | null
  scopeType?: string | null
  scopeId?: string | null
  scopeLabel?: string | null
  scopePath?: string | null
}

type AskAiOptions = {
  text?: string
  activityContext?: Record<string, unknown>
  metadata?: Record<string, unknown>
  page?: string | null
  pageSummary?: string | null
  pageData?: Record<string, unknown> | null
  pagePath?: string
  pageUrl?: string
  action?: string
  delayMs?: number
  threadName?: string
  threadScope?: AskAiThreadScope
  /**
   * When false, open the assistant and prepare the thread but don't send
   * an opening message. If text is supplied, prefill the composer for review.
   * Otherwise let the user type their own question. Defaults to true.
   */
  sendMessage?: boolean
}

const DEFAULT_ASK_AI_TEXT = 'What can you help me with on this page?'

function toRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }

  return value as Record<string, unknown>
}

function toNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function cleanScopeLabel(label: string | null | undefined, fallback: string): string {
  const cleaned = (label ?? '')
    .replace(/^user is viewing\s+/i, '')
    .replace(/^viewing\s+/i, '')
    .replace(/\.$/, '')
    .trim()

  if (!cleaned) return fallback
  const normalized = cleaned.toLowerCase()
  if (normalized === 'page' || normalized === 'page purpose summary') {
    return fallback
  }

  return cleaned
}

function matchesEntityPath(pagePath: string, entity: string): boolean {
  return new RegExp(`/(?:screenata/|compliance/)?${entity}/[^/]+`).test(pagePath)
}

function extractPageData(pageContext: unknown): Record<string, unknown> {
  const context = toRecord(pageContext)
  const data = toRecord(context.data)
  const extraEntries = Object.entries(context).filter(
    ([key]) => key !== 'page' && key !== 'summary' && key !== 'data'
  )

  return {
    ...data,
    ...Object.fromEntries(extraEntries),
  }
}

function inferThreadScope({
  explicitScope,
  page,
  pageSummary,
  pageData,
  pagePath,
}: {
  explicitScope?: AskAiThreadScope
  page?: string | null
  pageSummary?: string | null
  pageData: Record<string, unknown>
  pagePath: string
}): ChatThreadScope | null {
  const explicitWorkspaceId =
    toNonEmptyString(explicitScope?.workspaceId) ??
    getWorkspaceIdFromPath(pagePath) ??
    undefined
  const explicitScopeType = toNonEmptyString(explicitScope?.scopeType)
  const explicitScopeId = toNonEmptyString(explicitScope?.scopeId)

  if (explicitWorkspaceId && explicitScopeType && explicitScopeId) {
    return {
      workspaceId: explicitWorkspaceId,
      scopeType: explicitScopeType,
      scopeId: explicitScopeId,
      scopeLabel:
        toNonEmptyString(explicitScope?.scopeLabel) ??
        cleanScopeLabel(pageSummary, explicitScopeType),
      scopePath: toNonEmptyString(explicitScope?.scopePath) ?? pagePath,
    }
  }

  const workspaceId = getWorkspaceIdFromPath(pagePath)
  if (!workspaceId) return null

  const pageName = toNonEmptyString(page) ?? 'page'
  const controlDefId = toNonEmptyString(pageData.controlDefId)
  const controlId = toNonEmptyString(pageData.controlId)
  const testId = toNonEmptyString(pageData.testId)
  const testTitle = toNonEmptyString(pageData.testTitle)
  const taskCode = toNonEmptyString(pageData.taskCode)
  const riskId = toNonEmptyString(pageData.riskId)
  const policySlug = toNonEmptyString(pageData.policySlug)
  const policyDocumentId = toNonEmptyString(pageData.policyDocumentId)
  const documentId =
    toNonEmptyString(pageData.documentId) ?? toNonEmptyString(pageData.docId)

  if (controlDefId || (controlId && matchesEntityPath(pagePath, 'controls'))) {
    return {
      workspaceId,
      scopeType: 'control-detail',
      scopeId: controlDefId ?? controlId!,
      // The control page's summary is "<display code> — <title>"; controlId is the internal SC-* id.
      scopeLabel: cleanScopeLabel(pageSummary, 'Control'),
      scopePath: pagePath,
    }
  }

  if (testId || matchesEntityPath(pagePath, 'tests')) {
    return {
      workspaceId,
      scopeType: 'test-detail',
      scopeId: testId ?? pagePath,
      scopeLabel: testTitle
        ? `${testTitle}${taskCode ? ` (${taskCode})` : ''}`
        : cleanScopeLabel(pageSummary, 'Test'),
      scopePath: pagePath,
    }
  }

  if (riskId || matchesEntityPath(pagePath, 'risks')) {
    return {
      workspaceId,
      scopeType: 'risk-detail',
      scopeId: riskId ?? pagePath,
      scopeLabel: cleanScopeLabel(pageSummary, 'Risk'),
      scopePath: pagePath,
    }
  }

  if (policySlug || policyDocumentId || matchesEntityPath(pagePath, 'policies')) {
    return {
      workspaceId,
      scopeType: 'policy-detail',
      scopeId: policySlug ?? policyDocumentId ?? pagePath,
      scopeLabel: cleanScopeLabel(pageSummary, 'Policy'),
      scopePath: pagePath,
    }
  }

  if (documentId || matchesEntityPath(pagePath, 'documents')) {
    return {
      workspaceId,
      scopeType: 'document-detail',
      scopeId: documentId ?? pagePath,
      scopeLabel: cleanScopeLabel(pageSummary, 'Document'),
      scopePath: pagePath,
    }
  }

  return {
    workspaceId,
    scopeType: pageName,
    scopeId: pagePath,
    scopeLabel: cleanScopeLabel(pageSummary, pageName),
    scopePath: pagePath,
  }
}

export function useAskAi() {
  const location = useLocation()
  const pageContext = useAtomValue(screenataPageContextAtom)
  const setAssistantOpen = useSetAtom(screenataAssistantOpenAtom)
  const setChatMessage = useSetAtom(chatProgrammaticMessageAtom)
  const createThread = useSetAtom(createThreadAtom)

  return useCallback(
    async (options: AskAiOptions = {}) => {
      const pageUrl =
        options.pageUrl ??
        (typeof window !== 'undefined'
          ? window.location.href
          : location.pathname)
      const pagePath = options.pagePath ?? location.pathname
      const page = options.page ?? pageContext?.page ?? null
      const pageSummary = options.pageSummary ?? pageContext?.summary ?? null
      const pageData = {
        ...extractPageData(pageContext),
        ...(options.pageData ?? {}),
      }
      const threadScope = inferThreadScope({
        explicitScope: options.threadScope,
        page,
        pageSummary,
        pageData,
        pagePath,
      })

      if (threadScope?.workspaceId && threadScope.scopeType && threadScope.scopeId) {
        // Always start a FRESH thread — never reuse a prior thread for the same
        // scope (e.g. the same testId). Reuse made a "new" Collect-Evidence chat
        // inherit the previous session's working memory (server working memory
        // is keyed by threadId), which leaked stale context across sessions.
        // Old threads remain in the list for manual reopen.
        await createThread({
          name:
            options.threadName ??
            threadScope.scopeLabel ??
            pageSummary ??
            'New chat',
          scope: {
            ...threadScope,
            lastAskAiAt: new Date().toISOString(),
          },
        })
      }

      setAssistantOpen(true)

      if (options.sendMessage === false) {
        if (options.text) {
          setChatMessage({ text: options.text, draft: true })
        }
        return
      }

      window.setTimeout(() => {
        setChatMessage({
          text: options.text ?? DEFAULT_ASK_AI_TEXT,
          metadata: options.metadata,
          body: {
            turnContext: {
              pageContext: {
                page: page ?? 'unknown',
                summary: pageSummary ?? '',
                data: {
                  action: options.action ?? 'askAi',
                  pageUrl,
                  pagePath,
                  ...pageData,
                  ...(options.activityContext ?? {}),
                },
              },
            },
          },
        })
      }, options.delayMs ?? 250)
    },
    [
      createThread,
      location.pathname,
      pageContext,
      setAssistantOpen,
      setChatMessage,
    ]
  )
}
